/**
 * Accounts API service
 * Feature: 015-manual-crud-entities - User Story 1
 * Feature: 020-aws-production-readiness - Updated to route through API Gateway
 */

import axios from 'axios';
import type {
  Account,
  CreateAccountRequest,
  UpdateAccountRequest,
  AccountSearchResult,
} from '../types/account';

export interface AccountPartnerAssociationResponse {
  accountId: string;
  partnerId?: string | null;
  linkedAt?: string | null;
  linkedByUserId?: string | null;
}

// Create axios instance with auth headers - routes through API Gateway at /api
const axiosInstance = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

axiosInstance.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('id_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.data) {
    if (typeof config.data.phone === 'string') {
      config.data.phone = config.data.phone.replace(/[^\d+]/g, '');
    }
    // Catch nested configurations if some forms bundle it up
    if (config.data.request && typeof config.data.request.phone === 'string') {
      config.data.request.phone = config.data.request.phone.replace(/[^\d+]/g, '');
    }
  }

  return config;
});

/**
 * Create a new account
 * @returns Created account with ETag in response headers
 */
export async function createAccount(
  request: CreateAccountRequest
): Promise<{ account: Account; etag: string }> {
  const response = await axiosInstance.post<Account>('/admin/accounts', request);
  
  const etag = response.headers['etag'] || '';
  return { account: response.data, etag };
}

/**
 * Get account by ID
 * @returns Account with ETag in response headers
 */
export async function getAccountById(id: string): Promise<{ account: Account; etag: string }> {
  const response = await axiosInstance.get<Account>(`/admin/accounts/${id}`);
  
  const etag = response.headers['etag'] || '';
  return { account: response.data, etag };
}

/**
 * Update an existing account (full replacement)
 * @param id Account ID (ULID)
 * @param request Update payload with all required fields
 * @param etag ETag from previous GET/POST for optimistic concurrency control
 * @returns Updated account with new ETag
 * @throws 412 Precondition Failed if account was modified by another user
 * @throws 409 Conflict if email/phone already exists for another account
 */
export async function updateAccount(
  id: string,
  request: UpdateAccountRequest,
  etag?: string
): Promise<{ account: Account; etag: string }> {
  const headers: Record<string, string> = {};
  if (etag) {
    headers['If-Match'] = etag;
  }

  const response = await axiosInstance.put<Account>(
    `/admin/accounts/${id}`,
    request,
    { headers }
  );

  const newEtag = response.headers['etag'] || '';
  return { account: response.data, etag: newEtag };
}

/**
 * List all accounts with optional status filter
 * @param status Optional filter: 'ACTIVE' | 'ARCHIVED'
 */
/**
 * List accounts with optional filtering and search
 * Feature 018: Added search parameter for client dropdown
 */
export async function listAccounts(options?: {
  status?: 'ACTIVE' | 'ARCHIVED';
  search?: string;
}): Promise<Account[]> {
  const params: Record<string, string> = {};
  if (options?.status) params.status = options.status;
  if (options?.search) params.search = options.search;
  
  const response = await axiosInstance.get<Account[]>('/admin/accounts', { params });
  return response.data;
}

/**
 * Search accounts by company name (debounced in AccountSelector component)
 * @param query Company name search term (minimum 2 characters)
 * @returns Array of matching accounts (limited to 20 results)
 */
export async function searchAccounts(query: string): Promise<AccountSearchResult[]> {
  if (query.length < 2) return [];
  
  const response = await axiosInstance.get<AccountSearchResult[]>('/intake/accounts/search', {
    params: { q: query, limit: 20 },
  });
  return response.data;
}

export async function updateAccountPartnerAssociation(
  accountId: string,
  partnerId: string | null,
  etag: string
): Promise<{ association: AccountPartnerAssociationResponse; etag: string }> {
  const response = await axiosInstance.patch<AccountPartnerAssociationResponse>(
    `/admin/accounts/${accountId}/partner-association`,
    { partnerId },
    {
      headers: {
        'If-Match': etag,
      },
    }
  );

  return {
    association: response.data,
    etag: response.headers['etag'] || '',
  };
}
